import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import ParticleNetwork from "../components/ParticleNetwork";
import { Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import ContactForm from "../components/ContactForm";
import ContactInfo from "../components/ContactInfo";
import MapEmbed from "../components/MapEmbed";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { FAQS } from "../data/site";

export default function Contact() {
  useSEO({
    title: "Contact ATS IT Solutions | Bulandshahr, UP",
    description:
      "Contact ATS IT Solutions in Bulandshahr, UP for IT consulting, software, cloud and AI solutions. Call, email or send us a message — we respond within one business day.",
  });

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 z-0 opacity-50">
          <ParticleNetwork className="w-full h-full" density={0.6} />
        </div>
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[400px] rounded-full bg-brand-orange/10 blur-[160px] z-0" />
        <div className="container-x relative z-10 text-center flex flex-col items-center">
          <Reveal>
            <span className="overline">Contact ATS IT Solutions</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-4">
              Let's <span className="text-gradient-orange">talk</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Have a project in mind or need a technology partner? Reach out to the ATS team — we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Two-column */}
      <section className="relative py-16 md:py-20 bg-navy">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7 glass rounded-3xl p-8 md:p-10">
            <h2 className="font-head text-2xl font-semibold text-white mb-2">Send us a message</h2>
            <p className="text-slate-400 text-sm mb-8">Fields marked * are required.</p>
            <ContactForm />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <ContactInfo />
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section data-testid="map-section" className="relative bg-navy pb-16 md:pb-20">
        <div className="container-x">
          <MapEmbed height="440px" />
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="relative py-20 md:py-28 bg-navy-deep">
        <div className="container-x max-w-3xl">
          <SectionHeading
            align="center"
            overline="FAQ"
            title="Before you reach out"
            subtitle="Quick answers to common questions."
          />
          <div className="mt-12">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-${i}`}
                  className="glass rounded-2xl px-6 border-white/10"
                >
                  <AccordionTrigger className="text-left font-head text-white hover:no-underline hover:text-brand-orange py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Reassurance strip */}
      <section className="relative py-16 bg-navy overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.04] pointer-events-none">
          <span className="font-head text-[18vw] font-bold text-white whitespace-nowrap">RELIABLE</span>
        </div>
        <div className="container-x relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-[2rem] p-10 text-center flex flex-col items-center"
          >
            <p className="font-head text-xl md:text-2xl text-white max-w-2xl leading-snug">
              16+ years of enterprise technology experience — delivered end to end.
            </p>
            <Link to="/about" data-testid="reassurance-about-link" className="mt-6 inline-flex items-center gap-2 text-brand-orange font-semibold hover:gap-3 transition-all">
              Learn more about ATS <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
